<script setup lang="ts">
import type { ThemeSettings, YouTubeEmbedSettings } from '~/stores/auth'
import { DEFAULT_YOUTUBE_EMBED_SETTINGS } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'
import { useCardHover } from '~/composables/useCardHover'

interface YouTubeVideo {
  id: string
  title: string
  thumbnailUrl: string
  publishedAt: string
  viewCount: string
  url: string
}

interface YouTubeChannelData {
  channelId: string
  channelTitle: string
  channelAvatarUrl: string | null
  channelBannerUrl: string | null
  subscriberCount: string
  videoCount: string
  viewCount: string
  channelUrl: string
  videos: YouTubeVideo[]
}

const props = defineProps<{
  characterId: string
  themeSettings: ThemeSettings
  youtubeSettings?: YouTubeEmbedSettings
  cardStyle?: Record<string, string>
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-youtube-settings': []
}>()

const skinPreview = inject<boolean>('skinPreview', false)
const { onCardMouseMove, onCardMouseLeave } = useCardHover(toRef(props, 'themeSettings'))

const yt = computed<YouTubeEmbedSettings>(() => ({
  ...DEFAULT_YOUTUBE_EMBED_SETTINGS,
  ...(props.youtubeSettings ?? {})
}))

const channelData = ref<YouTubeChannelData | null>(null)
const loaded = ref(false)

async function fetchChannelData() {
  const params: Record<string, string> = {}
  if (yt.value.displayMode === 'pinned' && yt.value.pinnedVideoIds.length > 0) {
    params.pinnedIds = yt.value.pinnedVideoIds.join(',')
  } else {
    params.videoCount = String(yt.value.videoCount)
  }
  channelData.value = await $fetch<YouTubeChannelData>(`/api/youtube/channel/${props.characterId}`, { params })
}

onMounted(async () => {
  if (skinPreview) {
    loaded.value = true
    return
  }
  try {
    await fetchChannelData()
  } catch {
    // Silent fail - component won't render
  } finally {
    loaded.value = true
  }
})

// Re-fetch when settings change (e.g. after saving embed settings)
watch(() => props.youtubeSettings, async () => {
  if (!loaded.value) return
  try {
    await fetchChannelData()
  } catch {
    // Silent fail
  }
}, { deep: true })

const cardBg = computed(() => {
  const rgb = hexToRgb(props.themeSettings.cardColor)
  return `rgba(${rgb.join(',')}, ${Math.min(props.themeSettings.cardOpacity / 100, 0.6)})`
})

const showBanner = computed(() => yt.value.showBanner !== false)

// Videos grid vs carousel
const visibleVideos = computed(() => channelData.value?.videos ?? [])
const useCarousel = computed(() => visibleVideos.value.length > 3)

// Carousel state
const activeIndex = ref(0)
const carouselItems = computed(() =>
  visibleVideos.value.map(v => ({ ...v, class: 'basis-1/3' }))
)
function onClickPrev() {
  activeIndex.value--
}
function onClickNext() {
  activeIndex.value++
}
function onSelect(index: number) {
  activeIndex.value = index
}

function formatCount(count: string): string {
  const num = parseInt(count, 10)
  if (isNaN(num)) return '0'
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return num.toLocaleString()
}

function formatViewCount(count: string): string {
  return `${formatCount(count)} views`
}
</script>

<template>
  <SkinPreviewPlaceholder
    v-if="skinPreview"
    label="YouTube Embed"
    icon="i-simple-icons-youtube"
    :card-style="cardStyle"
    :theme-settings="themeSettings"
  />
  <div
    v-else-if="loaded && channelData"
    class="group rounded-xl overflow-hidden"
    :style="{
      background: cardBg,
      ...cardStyle
    }"
    @mousemove="onCardMouseMove"
    @mouseleave="onCardMouseLeave"
  >
    <!-- Channel banner -->
    <div
      v-if="showBanner && channelData.channelBannerUrl"
      class="w-full h-20 overflow-hidden"
    >
      <img
        :src="`${channelData.channelBannerUrl}=w1060-fcrop64=1,00005a57ffffa5a8-k-c0xffffffff-no-nd-rj`"
        :alt="`${channelData.channelTitle} banner`"
        class="w-full h-full object-cover"
        loading="lazy"
        referrerpolicy="no-referrer"
      >
    </div>

    <!-- Channel header -->
    <a
      :href="channelData.channelUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2.5 px-3 pt-3 pb-2 no-underline group/header"
    >
      <img
        v-if="channelData.channelAvatarUrl"
        :src="channelData.channelAvatarUrl"
        :alt="channelData.channelTitle"
        class="size-10 rounded-full shrink-0 object-cover"
        referrerpolicy="no-referrer"
      >
      <div
        v-else
        class="size-10 rounded-full bg-[#FF0000]/20 flex items-center justify-center shrink-0"
      >
        <UIcon
          name="i-simple-icons-youtube"
          class="size-4 text-[#FF0000]"
        />
      </div>
      <div class="min-w-0 flex-1">
        <span class="text-sm font-semibold text-white leading-tight block truncate group-hover/header:text-[#FF0000] transition-colors">
          {{ channelData.channelTitle }}
        </span>
        <span class="text-xs text-white/40 leading-tight block">
          {{ formatCount(channelData.subscriberCount) }} subscribers
          <span class="text-white/20 mx-0.5">&middot;</span>
          {{ formatCount(channelData.videoCount) }} videos
        </span>
      </div>
    </a>

    <!-- Videos: grid (<=3) -->
    <div
      v-if="visibleVideos.length > 0 && !useCarousel"
      class="px-3 pb-2"
    >
      <div class="grid grid-cols-3 gap-2">
        <a
          v-for="video in visibleVideos"
          :key="video.id"
          :href="video.url"
          target="_blank"
          rel="noopener noreferrer"
          class="group/video no-underline"
        >
          <div class="relative aspect-video rounded-lg overflow-hidden bg-white/5">
            <img
              :src="video.thumbnailUrl"
              :alt="video.title"
              class="w-full h-full object-cover group-hover/video:scale-105 transition-transform duration-200"
              loading="lazy"
            >
            <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover/video:opacity-100 transition-opacity duration-200">
              <UIcon
                name="i-lucide-external-link"
                class="size-4 text-white"
              />
            </div>
          </div>
          <p class="text-[11px] text-white/60 leading-tight mt-1 line-clamp-2 h-[2.4em] group-hover/video:text-white/80 transition-colors">
            {{ video.title }}
          </p>
          <span class="text-[10px] text-white/30">{{ formatViewCount(video.viewCount) }}</span>
        </a>
      </div>
    </div>

    <!-- Videos: carousel (>3) -->
    <div
      v-else-if="visibleVideos.length > 0 && useCarousel"
      class="px-3 pb-2 overflow-hidden"
    >
      <UCarousel
        v-slot="{ item }"
        :items="carouselItems"
        arrows
        :ui="{ prev: 'start-2 sm:start-2 top-1/3', next: 'end-2 sm:end-2 top-1/3' }"
        :prev="{ onClick: onClickPrev, size: 'xs' }"
        :next="{ onClick: onClickNext, size: 'xs' }"
        @select="onSelect"
      >
        <a
          :href="(item as unknown as YouTubeVideo).url"
          target="_blank"
          rel="noopener noreferrer"
          class="group/video no-underline"
        >
          <div class="relative aspect-video rounded-lg overflow-hidden bg-white/5">
            <img
              :src="(item as unknown as YouTubeVideo).thumbnailUrl"
              :alt="(item as unknown as YouTubeVideo).title"
              class="w-full h-full object-cover group-hover/video:scale-105 transition-transform duration-200"
              loading="lazy"
            >
            <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover/video:opacity-100 transition-opacity duration-200">
              <UIcon
                name="i-lucide-external-link"
                class="size-4 text-white"
              />
            </div>
          </div>
          <p class="text-[11px] text-white/60 leading-tight mt-1 line-clamp-2 h-[2.4em] group-hover/video:text-white/80 transition-colors">
            {{ (item as unknown as YouTubeVideo).title }}
          </p>
          <span class="text-[10px] text-white/30">{{ formatViewCount((item as unknown as YouTubeVideo).viewCount) }}</span>
        </a>
      </UCarousel>
    </div>

    <!-- Footer with YouTube branding (required by ToS) -->
    <div class="flex items-center justify-end px-3 pb-2.5">
      <UIcon
        name="i-simple-icons-youtube"
        class="size-3 text-white/15 group-hover:text-[#FF0000] transition-colors"
      />
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="w-full flex items-center justify-center gap-2 py-2 px-4 border-t border-white/6 transition-all duration-200 hover:bg-white/4 cursor-pointer"
      :style="{ color: 'rgba(255, 255, 255, 0.4)' }"
      @click="emit('open-youtube-settings')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage embed</span>
    </button>
  </div>
</template>
